/** Post-enrichment validation calls: banner, "View products" and revalidate on integration-service
 *  (enveloped); the action gates and the repair ("Fix automatically") on workflow-service. */
import { integrationsClient, workflowRunsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { ApiError } from '~/api/errors';

import type {
  ProductsValidationSummary,
  EnrichmentValidatedProduct,
  EnrichmentValidationFacets,
  EnrichmentValidationFieldChange,
  EnrichmentValidationProductFilters,
  EnrichmentValidationProductsPage,
  EnrichmentValidationProductsQuery,
  EnrichmentValidationRun,
  EnrichmentValidationSummary,
  IntegrationPaginatedResponse,
  IntegrationResponse,
  RepairRequest,
  RepairResponse,
  ScopedValidatedProduct,
  ScopedValidatedProductsPage,
  ScopedValidationFacets,
  ValidationActionCapabilities,
  ValidationScope,
  ValidationActionsRunDetail,
} from '../types/enrichment-validation.api.types';

/** A products row as the wire may send it: `changes` is missing before integration-service
 *  records the before / after. */
type WireValidatedProduct = Omit<EnrichmentValidatedProduct, 'changes'> & {
  changes?: (Omit<EnrichmentValidationFieldChange, 'applied'> & { applied?: boolean })[];
};

/** The summary as the wire may send it: `normalisedProducts` is missing before
 *  integration-service counts it. */
type WireValidationSummary = Omit<EnrichmentValidationSummary, 'normalisedProducts' | 'progress'> &
  Partial<Pick<EnrichmentValidationSummary, 'normalisedProducts' | 'progress'>>;

/** Connectors the rule set covers — integration-service answers any other with a 422. Every
 *  call sends the run's own connector rather than relying on the server's GOOGLE_MERCHANT default. */
export type ValidationConnectorCode = 'GOOGLE_MERCHANT' | 'META_COMMERCE';

/** The validation connector for a short (`GMC` / `META`) or integration (`GOOGLE_MERCHANT` /
 *  `META_COMMERCE`) connector code; `null` where validation does not apply (Shopify, file, …). */
export function toValidationConnector(
  code: 'GMC' | 'META' | ValidationConnectorCode,
): ValidationConnectorCode;
export function toValidationConnector(
  code: string | null | undefined,
): ValidationConnectorCode | null;
export function toValidationConnector(
  code: string | null | undefined,
): ValidationConnectorCode | null {
  switch (code) {
    case 'GMC':
    case 'GOOGLE_MERCHANT':
      return 'GOOGLE_MERCHANT';
    case 'META':
    case 'META_COMMERCE':
      return 'META_COMMERCE';
    default:
      return null;
  }
}

/** Whether runs on this connector (either code form) are validated — the one gate every screen uses. */
export function isValidationConnector(code: string | null | undefined): boolean {
  return toValidationConnector(code) !== null;
}

/** Rule id of a CRITICAL normalisation "Fix automatically" writes — one issue per action, worded
 *  as the agents' normaliser words it (contract §6 / §6.1). */
export const STRUCTURE_INVALID_RULE = 'structure_invalid';

/** Rule id of a WARNING: normalising would leave the field empty, so it is left as stored. */
export const NORMALISED_TO_EMPTY_RULE = 'normalised_to_empty';

/** The normalisation-only validator's rule ids (contract §6). Anything else is a retired id an
 *  older verdict may still carry until the run is re-validated. */
export const LIVE_VALIDATION_RULES: ReadonlySet<string> = new Set([
  STRUCTURE_INVALID_RULE,
  NORMALISED_TO_EMPTY_RULE,
  'internal_rule_error',
]);

/** Plain-text fallback label for a rule id this build has no copy for (a retired id): underscores
 *  and colons spaced, first letter capitalised — the contract says to render it as text. */
export function humanizeRuleId(rule: string): string {
  const spaced = rule.replace(/[_:]+/g, ' ').trim();
  return spaced === '' ? rule : spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Job `source`s whose runs store scores only: never validated, and revalidate answers 409. */
export const SCORES_ONLY_ENRICHMENT_SOURCES: ReadonlySet<string> = new Set([
  'INLINE',
  'FILE',
  'BATCH_UPLOAD',
]);

/** The banner summary, read by the run's root id (the server resolves its other ids). A 404 resolves
 *  `null` and hides the banner: never-validated is `200 NONE`, so 404 = route not served. */
export async function getEnrichmentValidationSummary(
  workflowId: string,
  connectorCode: ValidationConnectorCode,
): Promise<EnrichmentValidationSummary | null> {
  try {
    const response = await integrationsClient
      .get(endpoints.enrichmentValidation.summary(workflowId), {
        searchParams: { connectorCode },
      })
      .json<IntegrationResponse<WireValidationSummary>>();
    // `normalisedProducts` / `progress` are absent from an integration-service older than them.
    return {
      ...response.data,
      normalisedProducts: response.data.normalisedProducts ?? 0,
      progress: response.data.progress ?? null,
    };
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 404) return null;
    throw error;
  }
}

/** One "View products" page (latest verdict per product, by external id). Unset filters are
 *  omitted, not sent empty: the server pattern-checks them and `''` is a 422. */
export async function getEnrichmentValidationProducts(
  workflowId: string,
  connectorCode: ValidationConnectorCode,
  query: EnrichmentValidationProductsQuery,
): Promise<EnrichmentValidationProductsPage> {
  const searchParams: Record<string, string | number> = {
    connectorCode,
    status: query.status,
    sort: query.sort,
    page: query.page,
    size: query.size,
  };
  if (query.q) searchParams.q = query.q;
  if (query.rule) searchParams.rule = query.rule;
  if (query.field) searchParams.field = query.field;
  if (query.reenrich) searchParams.reenrich = query.reenrich;

  const response = await integrationsClient
    .get(endpoints.enrichmentValidation.products(workflowId), { searchParams })
    .json<IntegrationPaginatedResponse<WireValidatedProduct>>();
  // `changes` (and each one's `applied`) is absent from an integration-service older than the
  // before / after: none, and every listed change applied.
  const products = response.data.map((product) => ({
    ...product,
    changes: (product.changes ?? []).map((change) => ({
      ...change,
      applied: change.applied ?? true,
    })),
  }));
  return { products, pagination: response.pagination };
}

/** One page of a cross-run list: the scopes' products under the per-run list's filters. */
export async function searchValidatedProducts(
  scopes: readonly ValidationScope[],
  connectorCode: ValidationConnectorCode,
  query: EnrichmentValidationProductsQuery,
): Promise<ScopedValidatedProductsPage> {
  const response = await integrationsClient
    .post(endpoints.enrichmentValidation.productsSearch(), {
      searchParams: { connectorCode },
      json: {
        scopes,
        status: query.status,
        q: query.q,
        rule: query.rule,
        field: query.field,
        sort: query.sort,
        page: query.page,
        size: query.size,
      },
    })
    .json<IntegrationPaginatedResponse<WireValidatedProduct & { workflowId: string }>>();
  const products: ScopedValidatedProduct[] = response.data.map((product) => ({
    ...product,
    changes: (product.changes ?? []).map((change) => ({
      ...change,
      applied: change.applied ?? true,
    })),
  }));
  return { products, pagination: response.pagination };
}

/** A cross-run list's filter-menu counts and per-tab counts, under `status` and `q`. */
export async function getScopedValidationFacets(
  scopes: readonly ValidationScope[],
  connectorCode: ValidationConnectorCode,
  query: Pick<EnrichmentValidationProductFilters, 'status' | 'q'>,
): Promise<ScopedValidationFacets> {
  const response = await integrationsClient
    .post(endpoints.enrichmentValidation.productsSearchFacets(), {
      searchParams: { connectorCode },
      json: { scopes, status: query.status, q: query.q },
    })
    .json<IntegrationResponse<ScopedValidationFacets>>();
  return response.data;
}

/** The filter menus' counts (rules / fields / re-enrich fates) under one `status` and search;
 *  `q` is omitted when blank, as on the products route. */
export async function getEnrichmentValidationFacets(
  workflowId: string,
  connectorCode: ValidationConnectorCode,
  query: Pick<EnrichmentValidationProductFilters, 'status' | 'q'>,
): Promise<EnrichmentValidationFacets> {
  const searchParams: Record<string, string> = {
    connectorCode,
    status: query.status,
  };
  if (query.q) searchParams.q = query.q;

  const response = await integrationsClient
    .get(endpoints.enrichmentValidation.productFacets(workflowId), { searchParams })
    .json<IntegrationResponse<EnrichmentValidationFacets>>();
  return response.data;
}

/** A FULL re-check of stored content ("Run / Re-run validation" — a recompute; no agents).
 *  404 not this tenant's run · 409 running or scores-only · 422 no connection · 503 down. */
export async function revalidateEnrichmentRun(
  workflowId: string,
  connectorCode: ValidationConnectorCode,
  productIds?: readonly string[],
): Promise<EnrichmentValidationRun> {
  // With `productIds` (≤ 5 000): a SCOPED re-check of just those products of an already
  // validated run; a never-validated run still gets its first FULL validation.
  const response = await integrationsClient
    .post(endpoints.enrichmentValidation.revalidate(workflowId), {
      searchParams: { connectorCode },
      ...(productIds && productIds.length > 0 ? { json: { productIds } } : {}),
    })
    .json<IntegrationResponse<EnrichmentValidationRun>>();
  return response.data;
}

/** Validation counts over the given products' latest verdicts (≤ 5 000 ids) — never ids back. */
export async function getEnrichmentValidationProductsSummary(
  workflowId: string,
  connectorCode: ValidationConnectorCode,
  productIds: readonly string[],
): Promise<ProductsValidationSummary> {
  const response = await integrationsClient
    .post(endpoints.enrichmentValidation.productsSummary(workflowId), {
      searchParams: { connectorCode },
      json: { productIds },
    })
    .json<IntegrationResponse<ProductsValidationSummary>>();
  return response.data;
}

/** The run's Re-enrich and Fix gates from one workflow-service run-detail read. Each is `null`
 *  (not offered) on a 404 (untracked run) or when the server could not determine it. */
export async function getValidationActionCapabilities(
  runId: string,
): Promise<ValidationActionCapabilities> {
  try {
    const detail = await workflowRunsClient
      .get(endpoints.workflowRuns.detail(runId))
      .json<ValidationActionsRunDetail>();
    return {
      invalidItemsRetry: detail.invalidItemsRetry ?? null,
      repairAction: detail.repairAction ?? null,
    };
  } catch (error) {
    if (error instanceof ApiError && error.statusCode === 404) {
      return { invalidItemsRetry: null, repairAction: null };
    }
    throw error;
  }
}

/** "Fix automatically" for the run's root `runId`: workflow-service reads the repairs server-side.
 *  201 (incl. `applied: false`) · 404 · 409 · 502 · 503. */
export async function repairEnrichmentRun(
  runId: string,
  requestedBy: string | undefined,
  // `void`: the mutation's variables when a caller repairs the whole run (`mutate()`).
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  productIds?: readonly string[] | void,
): Promise<RepairResponse> {
  const body: RepairRequest = {
    ...(requestedBy ? { requestedBy } : {}),
    ...(productIds && productIds.length > 0 ? { productIds: [...productIds] } : {}),
  };
  return workflowRunsClient
    .post(endpoints.workflowRuns.repair(runId), { json: body })
    .json<RepairResponse>();
}
